import format from 'date-fns/format';
import {SyntheticEvent} from 'react';
import {SchemeLink} from '../../../applink/SchemeLink';
import {t} from '../../../common/getTranslations';
import {twMerge} from '../../../common/twMerge';
import {ExtraLabelType, HistoryType} from '../../../graphql/generated/types';
import {TranslateKey} from '../../../locale/type';
import {LOG_CLICK_EVENT} from '../../../logging/logEvent';
import {putTiaraObjectProps} from '../../../logging/putTiaraObjectProps';
import {Button} from '../../atoms/Button/Button';
import {ButtonColorScheme, ButtonSize} from '../../atoms/Button/Button.enum';
import {Icon} from '../../atoms/Icon/Icon';
import {IconType} from '../../atoms/Icon/IconType.enum';
import {Text} from '../../atoms/Text/Text';
import {ConditionalWrapper} from '../ConditionalWrapper/ConditionalWrapper';
import {HistoryItemProps} from './HistoryItemProps.interface';

export function HistoryItem({
  history,
  hasLink,
  hasCreditMasterId,
  shouldWarnExpired,
  onClickCashReceiptsResult,
}: HistoryItemProps) {
  if (!history) {
    return null;
  }

  // 기간만료 되었을때 전체 딤드 처리가 되어야 합니다.
  const isExpired = history.extraLabelType === ExtraLabelType.Expired;

  return (
    <div key={history.id} className="flex p-15pxr">
      <ConditionalWrapper
        condition={hasLink}
        WrapperComponent={SchemeLink}
        wrapperProps={{
          scheme: `kakaopage://open/content?series_id=${history.seriesId}`,
          linkClassName: twMerge(
            'flex w-full',
            !history.seriesId ? 'pointer-events-none cursor-default' : undefined
          ),
          onClick: (e: SyntheticEvent) => {
            LOG_CLICK_EVENT('상세내역_클릭', e);
          },
          ...putTiaraObjectProps({
            click: {
              layer1: '서브탭',
            },
          }),
        }}
      >
        <div className="flex flex-1 flex-col">
          <div className="flex justify-between">
            <div className="flex items-start">
              <Text
                className={twMerge(
                  'font-small1-bold break-all text-theme-solid-100 line-clamp-2',
                  isExpired &&
                    EXTRA_LABEL_TYPE_TEXT_COLOR_MAP[ExtraLabelType.Expired]
                )}
                style={{
                  color: history.purchasedItemColor
                    ? history.purchasedItemColor
                    : undefined,
                }}
              >
                {history.purchasedItem}
              </Text>
              {hasLink && (
                <Icon
                  type={IconType.ButtonArrowRight}
                  className="pt-3pxr"
                  disableActiveEffect
                />
              )}
            </div>
            {(history.productType || history.price) && (
              <div className="flex shrink-0 items-start pl-20pxr text-right">
                <Text
                  className={twMerge(
                    'text-theme-solid-100',
                    history.productType && 'font-small2',
                    !history.productType && 'font-small2-bold',
                    isExpired &&
                      EXTRA_LABEL_TYPE_TEXT_COLOR_MAP[ExtraLabelType.Expired]
                  )}
                  style={{
                    color: history.priceColor ? history.priceColor : undefined,
                  }}
                >
                  {history.productType ? history.productType : history.price}
                </Text>
              </div>
            )}
          </div>
          <div className="mt-2pxr flex">
            <Text
              className={twMerge(
                'font-small1 break-all text-theme-solid-100 line-clamp-2',
                isExpired &&
                  EXTRA_LABEL_TYPE_TEXT_COLOR_MAP[ExtraLabelType.Expired]
              )}
            >
              {history.detailText}
            </Text>
          </div>
          {history.detailText2 && (
            <div className="mt-2pxr flex">
              <Text
                className={twMerge(
                  'font-small1 break-all text-theme-solid-100 line-clamp-2',
                  isExpired &&
                    EXTRA_LABEL_TYPE_TEXT_COLOR_MAP[ExtraLabelType.Expired]
                )}
              >
                {history.detailText2}
              </Text>
            </div>
          )}
          <div className="mt-6pxr flex justify-between">
            <div>
              <Text
                className={twMerge(
                  'font-small2 break-all text-theme-solid-40 line-clamp-1',
                  isExpired &&
                    EXTRA_LABEL_TYPE_TEXT_COLOR_MAP[ExtraLabelType.Expired]
                )}
              >
                {history.historyType === HistoryType.CashCharging
                  ? history.displayDt &&
                    format(
                      new Date(history.displayDt),
                      t('date_formatter_short_year_time_dot')
                    )
                  : history.createDt &&
                    format(
                      new Date(history.createDt),
                      t('date_formatter_short_year_time_dot')
                    )}
              </Text>
            </div>
            <div className="flex shrink-0 flex-col space-y-6pxr pl-20pxr text-right">
              {history.extraLabelType && (
                <Text
                  className={twMerge(
                    'font-small2 line-clamp-2',
                    history.extraLabelType &&
                      EXTRA_LABEL_TYPE_TEXT_COLOR_MAP[history.extraLabelType]
                  )}
                >
                  {history.historyType === HistoryType.CashCharging ||
                  history.historyType === HistoryType.CashUsage
                    ? t(CASH_EXTRA_LABEL_TYPE_TITLE_MAP[history.extraLabelType])
                    : t(
                        TICKET_EXTRA_LABEL_TYPE_TITLE_MAP[
                          history.extraLabelType
                        ]
                      )}
                </Text>
              )}
              {hasCreditMasterId && (
                <div>
                  <Button
                    size={ButtonSize.Small}
                    colorScheme={ButtonColorScheme['3rd']}
                    onClick={() =>
                      onClickCashReceiptsResult &&
                      onClickCashReceiptsResult(history.creditMasterId)
                    }
                  >
                    {t('cash_history_receipt')}
                  </Button>
                </div>
              )}
              {!history.extraLabelType && history.expiredAt && (
                <Text
                  className={twMerge(
                    'font-small2 text-theme-solid-100',
                    shouldWarnExpired && 'text-static-point-red'
                  )}
                >
                  {t('giftbox_gift_expiry_date', {
                    values: {
                      value: format(
                        new Date(history.expiredAt),
                        t('date_formatter_short_year_time_dot')
                      ),
                    },
                  })}
                </Text>
              )}
            </div>
          </div>
        </div>
      </ConditionalWrapper>
    </div>
  );
}

const TICKET_EXTRA_LABEL_TYPE_TITLE_MAP: {
  [key in ExtraLabelType]: TranslateKey;
} = {
  [ExtraLabelType.CancelCharge]: 'more_ticket_history_cancel_purchase',
  [ExtraLabelType.ExtinctCash]: 'more_ticket_history_disappeard',
  [ExtraLabelType.RefundTicket]: 'more_ticket_history_refund',
  [ExtraLabelType.WithdrawByAdmin]: 'more_ticket_history_retrieved_cash',
  [ExtraLabelType.Expired]: 'more_ticket_history_period_expired',
  [ExtraLabelType.CancelIapMarket]:
    'more_ticket_history_retrieved_store_refundcash',
  [ExtraLabelType.WithdrawByAdminPlayPoints]:
    'cash_history_retrieved_playpoints',
};

const CASH_EXTRA_LABEL_TYPE_TITLE_MAP: {
  [key in ExtraLabelType]: TranslateKey;
} = {
  [ExtraLabelType.CancelCharge]: 'cash_history_add_cancel',
  [ExtraLabelType.WithdrawByAdmin]: 'cash_history_add_recovery',
  [ExtraLabelType.Expired]: 'cash_history_expired',
  [ExtraLabelType.CancelIapMarket]: 'cash_history_retrieved_store_refundcash',
  [ExtraLabelType.ExtinctCash]: 'cash_history_disappeard',
  [ExtraLabelType.WithdrawByAdminPlayPoints]:
    'cash_history_retrieved_playpoints',
  /**
   * 실제론 getCashChargingExtraLabel에서 Refund도 ExtraLabelType.CancelCharge으로 리턴하기 때문에 존재하지 않지만 타입을 위해 CancelCharge와 동일한 번역키 세팅해놓았습니다.
   */
  [ExtraLabelType.RefundTicket]: 'cash_history_add_cancel',
};

const EXTRA_LABEL_TYPE_TEXT_COLOR_MAP: {[key in ExtraLabelType]: string} = {
  [ExtraLabelType.CancelCharge]: 'text-static-point-red',
  [ExtraLabelType.ExtinctCash]: 'text-theme-solid-100',
  [ExtraLabelType.RefundTicket]: 'text-static-point-red',
  [ExtraLabelType.WithdrawByAdmin]: 'text-static-point-red',
  [ExtraLabelType.Expired]: 'text-theme-solid-40',
  [ExtraLabelType.CancelIapMarket]: 'text-static-point-red',
  [ExtraLabelType.WithdrawByAdminPlayPoints]: 'text-static-point-red',
};
